import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const Privacy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Login
        </button>

        <div className="bg-white rounded-lg shadow-md p-8">
          <h1 className="text-3xl font-bold mb-6 text-slate-900">Privacy Policy</h1>
          <p className="text-slate-600 mb-6">Last updated: December 2024</p>

          <div className="space-y-6 text-slate-700">
            <div>
              <h2 className="text-xl font-semibold mb-3 text-slate-900">What we collect</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Email address for account creation and authentication</li>
                <li>Name and school information (optional, provided during onboarding)</li>
                <li>Grade level and subject preferences to personalize your experience</li>
                <li>Lesson plans you create using our platform</li>
                <li>Usage statistics (lessons generated, features used) to improve the service</li>
                <li>Basic technical information (browser type, device information) for troubleshooting</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3 text-slate-900">How we use your information</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>To provide and maintain the LessonlyAI service</li>
                <li>To generate personalized, TEKS-aligned lesson plans</li>
                <li>To communicate with you about your account and service updates</li>
                <li>To improve our AI models and service quality</li>
                <li>To analyze usage patterns and enhance features</li>
                <li>To provide customer support</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3 text-slate-900">What we don't do</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Sell your personal data to third parties</li>
                <li>Share your lesson plans publicly without your permission</li>
                <li>Use your data for advertising purposes</li>
                <li>Share student information (we don't collect student data)</li>
                <li>Track you across other websites</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3 text-slate-900">Data security</h2>
              <p>
                We use industry-standard security measures to protect your data, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Encrypted connections (HTTPS/TLS)</li>
                <li>Secure authentication through Supabase</li>
                <li>Regular security updates and monitoring</li>
                <li>Access controls to protect your information</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3 text-slate-900">Your rights</h2>
              <p className="mb-2">You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access your personal data</li>
                <li>Delete your account and associated data</li>
                <li>Export your lesson plans</li>
                <li>Opt out of non-essential communications</li>
                <li>Request corrections to your information</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-3 text-slate-900">Third-party services</h2>
              <p>
                We use trusted third-party services to provide our platform:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li><strong>Supabase:</strong> Authentication and database hosting</li>
                <li><strong>Google Gemini AI:</strong> Lesson plan generation</li>
                <li><strong>Hosting providers:</strong> To serve the application</li>
              </ul>
              <p className="mt-2">
                These services have their own privacy policies and security measures.
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <h2 className="text-xl font-semibold mb-3">Contact us</h2>
              <p>
                Questions about our privacy policy or your data?{' '}
                <a
                  href="mailto:pablo@lessonlyai.com"
                  className="text-blue-600 hover:underline"
                >
                  pablo@lessonlyai.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
